// Vendor libs
import React, { useEffect, useState } from "react";
import moment from "moment";
import { useHistory } from "react-router-dom";

// Components & Utils
import MealPlanApi from "../api/MealPlans";
import MenuApi from "../api/Menus";
import { getDays } from "../utils/DateHelper";
import { useGlobalValue } from "../utils/GlobalState";
import { setIsLoading, setErrors } from "../utils/Actions";
import { TEMPERATURES } from "../utils/Constants";
import { getCutOffDate } from "../services/mealplans";
import AddMealCommentModal from "../components/AddMealCommentModal";

export default function NewRecMealPlan() {
  const [{ sysCfg, user }, dispatch] = useGlobalValue();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState();
  const [menuId, setMenuId] = useState(0);
  const [menus, setMenus] = useState([]);
  const [meals, setMeals] = useState([]);
  const [mealsSelected, setMealsSelected] = useState({});
  const [cutOffDate, setCutOffDate] = useState(moment());
  const [commentsModal, setCommentsModal] = useState({ show: false, meal: [] });
  const days = getDays();
  let history = useHistory();

  //on component mount
  useEffect(() => {
    setCutOffDate(getCutOffDate(sysCfg));
  }, [sysCfg]);

  // On Start Date Change
  useEffect(() => {
    if (start === "") {
      return;
    }

    const startDate = moment(start, "YYYY-MM-DD");
    setIsLoading(true, dispatch);

    MenuApi.getMenuActiveOn(startDate)
      .then(menus => {
        setMenus(menus);
        setIsLoading(false, dispatch);
      })
      .catch(apiErrors => {
        setErrors(apiErrors, dispatch);
        setIsLoading(false, dispatch);
      });
  }, [start, dispatch]);

  // On Menu Change
  useEffect(() => {
    if (+menuId === 0) {
      return;
    }

    setIsLoading(true, dispatch);
    MenuApi.getMealsForMenu(menuId, user.client_id)
      .then(meals => {
        setMeals(meals);
        setIsLoading(false, dispatch);
      })
      .catch(apiErrors => {
        setErrors(apiErrors, dispatch);
        setIsLoading(false, dispatch);
      });
  }, [menuId, user.client_id, dispatch]);

  // Checkbox On Change
  const handleMealSelect = (meal, selected) => {
    let _mealsSelected = mealsSelected;
    if (selected) {
      meal.menumeal_id = meal.id;

      //set default temperature to N/A if meal temperature is disabled
      if (sysCfg.mealordering_add_meal_show_temperature !== undefined && +sysCfg.mealordering_add_meal_show_temperature === 0) {
        meal.temperature = 4;
      }

      _mealsSelected[meal.id] = meal;
      setMealsSelected(_mealsSelected);
    } else {
      delete _mealsSelected[meal.id];
      setMealsSelected(_mealsSelected);
    }

    let _meals = meals;
    setMeals(_meals);
  };

  // On Meal Property Select
  const selectProperty = (key, property_id) => {
    let _mealsSelected = mealsSelected;
    _mealsSelected[key].property = property_id;
    setMealsSelected(_mealsSelected);
  };

  // On Meal Temp Select
  const selectTemp = (key, temp_id) => {
    let _mealsSelected = mealsSelected;
    _mealsSelected[key].temperature = temp_id;
    setMealsSelected(_mealsSelected);
  };

    // On Meal Qty Update
    const setQty = (key, qty_val) => {
      setIsLoading(true, dispatch);
      let _meals = meals;
      _meals[key].qty = qty_val;
    
      setMeals(_meals);
      setIsLoading(false, dispatch);
    };

  // Submit Button Action
  const submit = () => {
    setIsLoading(true, dispatch);

    MealPlanApi.newRecurringMealPlan(menuId, start, end, mealsSelected)
      .then(() => {
        setIsLoading(false, dispatch);
        history.push("/recmealplans");
      })
      .catch(apiErrors => {
        console.log(apiErrors);
        setErrors(apiErrors, dispatch);
        setIsLoading(false, dispatch);
      });
  };

  /**
   * Function to validate start date is not before cut off date or current date
   * @param {Object} event
   * @returns void
   */
  const validateStartDate = event => {
    const date = event.target.value;
    const selectedDate = moment(date, "YYYY-MM-DD").startOf('day');

    if (selectedDate.isAfter(cutOffDate)) {
      setStart(date);
    } else {
      setStart('');
      setErrors([`Please select a valid start date. It can not be equal or before the cut off date: ${cutOffDate.format('DD/MM/YYYY')}.`], dispatch);
    }
  }

  return (
    <div>
      <div className="row">
        <div className="col-sm-8">
          <h2>New Recurring Meal Plan</h2>
        </div>
        <div className="col-sm-4">
          <button className="btn btn-success btn-sm btn-block" onClick={() => submit()}>
            Submit
          </button>
        </div>
      </div>
      <br />

      <div className="row">
        <div className="col-sm-4">
          <label>Start Date:</label>
          <br />
          <input type="date" value={start} min={cutOffDate.format('YYYY-MM-DD')} onChange={e => validateStartDate(e)} />
        </div>
        <div className="col-sm-4">
          <label>
            End Date: <span style={{ color: "darkred" }}>** Optional</span>
          </label>
          <br />
          <input type="date" min={cutOffDate.format('YYYY-MM-DD')} onChange={e => setEnd(e.target.value)} />
        </div>
        <div className="col-sm-3">
          <label>Select a Menu:</label>
          <br />
          <select className="form-control" onChange={e => setMenuId(e.target.value)}>
            <option value="0">Please Select</option>
            {menus.map(menu => {
              return (
                <option key={menu.id} value={menu.id}>
                  {menu.name}
                </option>
              );
            })}
          </select>
        </div>
      </div>
      <br />

      <table className="table table-bordered" style={{ background: "white" }}>
        <thead>
          <tr>
            <th style={{ width: 30 }}></th>
            <th>Qty.</th>
            <th>Meal</th>
            {sysCfg.mealordering_properties && +sysCfg.mealordering_properties === 1 ? (
              <th style={{ width: "30%" }}>Type</th>
            ) : (
              <></>
            )}
            {sysCfg.mealordering_add_meal_show_temperature && +sysCfg.mealordering_add_meal_show_temperature === 1 ? (
              <th>Temperature</th>
            ) : (
              <></>
            )}
            <th>Week</th>
            <th>Day</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(meals).map(key => {
            const meal = meals[key];

            return (
              <tr key={meal.id}>
                <td>
                  <input type="checkbox" onChange={e => handleMealSelect(meal, e.target.checked)} />
                </td>
                <td><input type="number" className="form-control" value={meal.qty} onChange={e => setQty(key, e.target.value)} /></td>
                <td>{meal.meal_name}</td>
                {sysCfg.mealordering_properties && +sysCfg.mealordering_properties === 1 ? (
                  <td>
                    {meal.properties && meal.properties.length > 0 ? (
                      <select
                        className="form-control"
                        disabled={!mealsSelected.hasOwnProperty(meal.id)}
                        onChange={e => selectProperty(meal.id, e.target.value)}
                      >
                        <option value={0}>Please Select</option>
                        {meal.properties.map(property => {
                          return (
                            <option key={property.id} value={property.property_id}>
                              {property.name}
                            </option>
                          );
                        })}
                      </select>
                    ) : (
                      <></>
                    )}
                  </td>
                ) : (
                  <></>
                )}
                {sysCfg.mealordering_add_meal_show_temperature && +sysCfg.mealordering_add_meal_show_temperature === 1 ? (
                  <td>
                    {meal.temperatures && Object.keys(meal.temperatures).length > 0 ? (
                      <select
                        className="form-control"
                        disabled={!mealsSelected.hasOwnProperty(meal.id)}
                        onChange={e => selectTemp(meal.id, e.target.value)}
                      >
                        <option value={0}>Please Select</option>
                        {Object.keys(meal.temperatures).map(key => {
                          const temp_id = meal.temperatures[key];
                          return (
                            <option key={key} value={temp_id}>
                              {TEMPERATURES[temp_id]}
                            </option>
                          );
                        })}
                      </select>
                    ) : (
                      <></>
                    )}
                  </td>
                ) : (
                  <></>
                )}
                <td>{meal.menu_week}</td>
                <td>{days[meal.menu_day]}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <AddMealCommentModal
        show={commentsModal.show}
        meal={commentsModal.meal}
        hide={() => setCommentsModal({ show: false, meal: [] })}
      />
    </div>
  );
}
